/**
 * P&L по проєктах і напрямках — AGGREGATE pattern (UX γ Y-deep P,
 * 2026-05-22).
 *
 * Управлінський звіт. На відміну від форм створення документів і
 * регламентних звітів (Trial Balance), цей звіт автоматично агрегує
 * дані по ВСIХ orgs де у user'а є `reports.management.view`.
 *
 * UI:
 *  - НЕМАЄ dropdown «оберіть org» — звіт показує одразу
 *  - warning banner якщо різні валюти серед агрегованих orgs
 *  - breakdown section якщо більше 1 org
 *  - warning page якщо user не має permission у жодній org
 *
 * Backend: GET /projects/reports/pnl?organization_ids=<UUID>&...
 * (Б-mgmt.5 виняток E з K.1; per-org permission check у handler'і).
 */
import { useState } from 'react';
import type { ProjectKind, ProjectPLLine, ProjectPLReport } from '@/api/projects';
import { useReportAggregation } from '@/hooks/useReportAggregation';
import DatePopover from '@/components/DatePopover';

const fmt = (v: number) => v.toLocaleString('uk-UA', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

type KindFilter = 'ALL' | ProjectKind;

interface PnlLinesTableProps {
  lines: ProjectPLLine[];
  totals: { income: number; expense: number; result: number };
  compact?: boolean;
}

function PnlLinesTable({ lines, totals, compact = false }: PnlLinesTableProps) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: compact ? '0.85rem' : '0.9rem' }}>
      <thead>
        <tr style={{ background: 'var(--bg-soft-2)', textAlign: 'left' }}>
          <th style={{ padding: '0.5rem', width: 100 }}>Код</th>
          <th style={{ padding: '0.5rem' }}>Проект / напрям</th>
          <th style={{ padding: '0.5rem', width: 140, textAlign: 'right' }}>Доходи</th>
          <th style={{ padding: '0.5rem', width: 140, textAlign: 'right' }}>Витрати</th>
          <th style={{ padding: '0.5rem', width: 140, textAlign: 'right' }}>Результат</th>
          <th style={{ padding: '0.5rem', width: 80, textAlign: 'right' }}>Маржа %</th>
          <th style={{ padding: '0.5rem', width: 80, textAlign: 'right' }}>Док.</th>
        </tr>
      </thead>
      <tbody>
        {lines.length === 0 ? (
          <tr>
            <td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: 'var(--muted)' }}>
              Немає даних за період.
            </td>
          </tr>
        ) : (
          lines.map((l, i) => {
            const margin = l.income > 0 ? (l.result / l.income * 100) : 0;
            const resultColor = l.result > 0 ? 'var(--success)' :
                                 l.result < 0 ? 'var(--danger)' : 'var(--text)';
            return (
              <tr key={i} style={{
                borderTop: '1px solid var(--border-soft)',
                background: l.project_id ? 'transparent' : 'var(--bg-soft)',
                fontStyle: l.project_id ? 'normal' : 'italic',
              }}>
                <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>
                  {l.project_code ?? '—'}
                </td>
                <td style={{ padding: '0.5rem' }}>
                  {l.project_name}
                </td>
                <td style={{ padding: '0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {fmt(l.income)}
                </td>
                <td style={{ padding: '0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {fmt(l.expense)}
                </td>
                <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600,
                              color: resultColor, fontVariantNumeric: 'tabular-nums' }}>
                  {fmt(l.result)}
                </td>
                <td style={{ padding: '0.5rem', textAlign: 'right', color: resultColor }}>
                  {l.income > 0 ? `${margin.toFixed(1)}%` : '—'}
                </td>
                <td style={{ padding: '0.5rem', textAlign: 'right', color: 'var(--muted)' }}>
                  {l.transactions}
                </td>
              </tr>
            );
          })
        )}
        <tr style={{ background: 'var(--primary-soft)', fontWeight: 700,
                      borderTop: '2px solid var(--primary)' }}>
          <td colSpan={2} style={{ padding: '0.5rem' }}>УСЬОГО</td>
          <td style={{ padding: '0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
            {fmt(totals.income)}
          </td>
          <td style={{ padding: '0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
            {fmt(totals.expense)}
          </td>
          <td style={{ padding: '0.5rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
            {fmt(totals.result)}
          </td>
          <td style={{ padding: '0.5rem', textAlign: 'right' }}>
            {totals.income > 0
              ? `${(totals.result / totals.income * 100).toFixed(1)}%`
              : '—'}
          </td>
          <td></td>
        </tr>
      </tbody>
    </table>
  );
}

export default function ProjectPLReportPage() {
  const today = new Date();
  const [periodStart, setPeriodStart] = useState(
    new Date(today.getFullYear(), 0, 1).toISOString().slice(0, 10),
  );
  const [periodEnd, setPeriodEnd] = useState(today.toISOString().slice(0, 10));
  const [kindFilter, setKindFilter] = useState<KindFilter>('ALL');

  // UX γ Y-deep P: aggregate hook замість dropdown'а
  const {
    data,
    breakdownByOrg,
    warnings,
    accessibleOrgs,
    totalAccessibleOrgs,
    loading,
  } = useReportAggregation<ProjectPLReport>(
    '/projects/reports/pnl',
    'reports.management.view',
    {
      period_start: periodStart,
      period_end: periodEnd,
      kind: kindFilter === 'ALL' ? undefined : kindFilter,
    },
  );

  // Warning page: user не має permission у жодній org
  if (!loading && accessibleOrgs.length === 0) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <h2 style={{ marginTop: 0 }}>P&L по проєктах і напрямках</h2>
          <div style={{
            padding: '1rem', background: 'var(--badge-amber-bg)', borderRadius: 6,
            border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав переглядати управлінський звіт у жодній з
            ваших організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          P&L по {kindFilter === 'DIRECTION' ? 'напрямках' :
                   kindFilter === 'PROJECT' ? 'проєктах' : 'проєктах і напрямках'}
        </h2>
        <button onClick={() => window.print()}>🖨 Друкувати</button>
      </div>

      {/* Aggregation context — список org що увійшли */}
      <div style={{
        marginBottom: '0.75rem', fontSize: '0.85rem', color: 'var(--muted)',
      }}>
        Агреговано по {accessibleOrgs.length}{' '}
        {accessibleOrgs.length === 1 ? 'юрособі' : 'юрособах'}:{' '}
        {accessibleOrgs.map((o) => o.code).join(', ')}.
        {accessibleOrgs.length < totalAccessibleOrgs && (
          <> ({totalAccessibleOrgs - accessibleOrgs.length} ваших org не дозволяють управлінський звіт.)</>
        )}
      </div>

      {/* Currency warning banner */}
      {warnings.length > 0 && (
        <div style={{
          padding: '0.75rem 1rem', marginBottom: '1rem',
          background: 'var(--badge-amber-bg)', borderRadius: 6,
          border: '1px solid #fbbf24', color: 'var(--badge-amber-fg)',
        }}>
          ⚠ {warnings[0]}
        </div>
      )}

      <div className="doc-grid" style={{ marginBottom: '1rem' }}>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Період з</label>
          <DatePopover value={periodStart} onChange={(v) => setPeriodStart(v)} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Період по</label>
          <DatePopover value={periodEnd} onChange={(v) => setPeriodEnd(v)} />
        </div>
        <div className="field" style={{ gridColumn: 'span 4' }}>
          <label>Розріз</label>
          <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value as KindFilter)}>
            <option value="ALL">Усі (напрямки + проєкти)</option>
            <option value="DIRECTION">Тільки напрямки</option>
            <option value="PROJECT">Тільки проєкти</option>
          </select>
        </div>
      </div>

      {loading && (
        <p style={{ padding: '1rem', textAlign: 'center', color: 'var(--muted)' }}>
          Завантаження…
        </p>
      )}

      {data && !loading && (
        <>
          <PnlLinesTable lines={data.lines} totals={data.totals} />
          <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: 'var(--muted)' }}>
            ⓘ Курсивом — операції без прив'язки до проекту.
            Доходи = кредитові обороти класу 7. Витрати = дебетові обороти
            класу 9 і 90 (собівартість).
          </p>

          {/* Breakdown section: тільки якщо більше 1 org */}
          {breakdownByOrg.length > 1 && (
            <div style={{ marginTop: '2rem' }}>
              <h3 style={{ marginBottom: '0.5rem' }}>
                Деталізація по {breakdownByOrg.length} юрособах
              </h3>
              {breakdownByOrg.map((b) => {
                // b.data — { lines: ProjectPLLine[]; totals: {...} }
                const bd = b.data as unknown as {
                  lines: ProjectPLLine[];
                  totals: { income: number; expense: number; result: number };
                };
                return (
                  <div key={b.organization_id} style={{
                    marginBottom: '1.5rem',
                    padding: '0.75rem',
                    border: '1px solid var(--border-soft)',
                    borderRadius: 6,
                  }}>
                    <div style={{
                      display: 'flex', justifyContent: 'space-between',
                      marginBottom: '0.5rem', fontWeight: 600,
                    }}>
                      <span>
                        {b.organization_name} — {b.organization_code}
                      </span>
                      <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
                        Валюта: {b.currency}
                      </span>
                    </div>
                    <PnlLinesTable lines={bd.lines} totals={bd.totals} compact />
                  </div>
                );
              })}
            </div>
          )}

          {data.intragroup_note && (
            <p style={{ marginTop: '1rem', fontSize: '0.75rem', color: 'var(--muted)', fontStyle: 'italic' }}>
              {data.intragroup_note}
            </p>
          )}
        </>
      )}
    </div>
  );
}
